import React from 'react';
import { clsx } from 'clsx';
import { ChevronRight, Wrench, LogOut } from 'lucide-react';
import { RoleType } from '../../types/api';
import { AdminSection } from '../../types/admin';
import {
  AppTab,
  PRIMARY_NAV,
  ADMIN_GROUPS,
  canSeeNav,
  canSeeAdminConfig,
  canRaiseComplaint,
  RAISE_COMPLAINT_ICON,
} from '../../config/navigation';

interface SidebarProps {
  role: RoleType | undefined;
  activeTab: AppTab;
  activeAdminSection: AdminSection;
  collapsed?: boolean;
  onNavigate: (tab: AppTab) => void;
  onAdminNavigate: (section: AdminSection) => void;
  onRaiseComplaint: () => void;
  onLogout: () => void;
}

const itemBase =
  'relative w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/60';
const itemIdle =
  'border-transparent text-slate-400 hover:text-slate-200 hover:bg-slate-800/50';
const itemActive =
  'bg-sky-500/10 text-sky-400 border-sky-500/20 font-semibold';

export const Sidebar: React.FC<SidebarProps> = ({
  role,
  activeTab,
  activeAdminSection,
  collapsed = false,
  onNavigate,
  onAdminNavigate,
  onRaiseComplaint,
  onLogout,
}) => {
  const visiblePrimary = PRIMARY_NAV.filter((item) => canSeeNav(item, role));
  const showAdmin = canSeeAdminConfig(role);
  const showRaise = canRaiseComplaint(role);

  return (
    <div className="flex h-full flex-col">
      <div className={clsx('flex items-center gap-2.5 h-16 shrink-0 border-b border-slate-800', collapsed ? 'justify-center px-2' : 'px-4')}>
        <div className="p-2 bg-sky-500/10 text-sky-400 rounded-xl shrink-0">
          <Wrench className="w-5 h-5" />
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <p className="font-semibold text-sm text-slate-100 leading-tight truncate">NAD PROJECT</p>
            <p className="text-[10px] font-medium text-slate-400 truncate">{role || 'Maintenance'}</p>
          </div>
        )}
      </div>

      {showRaise && (
        <div className="px-3 pt-3 shrink-0">
          <button
            type="button"
            onClick={onRaiseComplaint}
            title={collapsed ? 'Raise Complaint' : undefined}
            aria-label="Raise Complaint"
            className={clsx(
              'flex items-center justify-center rounded-lg bg-sky-500 hover:bg-sky-600 active:bg-sky-700 text-white text-[13px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/60',
              collapsed ? 'w-10 h-10 mx-auto' : 'w-full gap-2 px-3 py-2'
            )}
          >
            {RAISE_COMPLAINT_ICON}
            {!collapsed && <span>Raise Complaint</span>}
          </button>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5" aria-label="Primary">
        {visiblePrimary.map((item) => {
          const isActive = activeTab === item.id && item.id !== 'admin-config';
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onNavigate(item.id)}
              aria-current={isActive ? 'page' : undefined}
              title={collapsed ? item.label : undefined}
              aria-label={collapsed ? item.label : undefined}
              className={clsx(itemBase, isActive ? itemActive : itemIdle, collapsed && 'justify-center')}
            >
              {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-sky-400" />}
              {item.icon}
              {!collapsed && <span className="truncate">{item.label}</span>}
              {isActive && !collapsed && <ChevronRight className="w-3 h-3 ml-auto shrink-0" />}
            </button>
          );
        })}

        {showAdmin && !collapsed && (
          <div className="pt-3 mt-3 border-t border-slate-800/70">
            {ADMIN_GROUPS.map((group) => (
              <div key={group.group}>
                <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mt-4 mb-1.5 px-3">
                  {group.group}
                </h4>
                <div className="space-y-0.5">
                  {group.items.map((item) => {
                    const isActive = activeTab === 'admin-config' && activeAdminSection === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => onAdminNavigate(item.id)}
                        aria-current={isActive ? 'page' : undefined}
                        className={clsx(itemBase, isActive ? itemActive : itemIdle)}
                      >
                        {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-sky-400" />}
                        {item.icon}
                        <span className="truncate">{item.label}</span>
                        {isActive && <ChevronRight className="w-3 h-3 ml-auto shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </nav>

      <div className="shrink-0 border-t border-slate-800 p-3">
        <button
          type="button"
          onClick={onLogout}
          title={collapsed ? 'Logout' : undefined}
          aria-label={collapsed ? 'Logout' : undefined}
          className={clsx(
            'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/60',
            collapsed && 'justify-center'
          )}
        >
          <LogOut className="w-4 h-4" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
};
